<script setup>
import { computed, ref, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToggle } from '@vueuse/core';
import { useAlert } from 'dashboard/composables';
import { useLocalizedTime } from 'shared/composables/useLocalizedTime';
import countries from 'shared/constants/countries';
import { useCompanyLabelsStore } from 'dashboard/stores/companyLabels';
import { useCompaniesStore } from 'dashboard/stores/companies';
import { useDealsStore } from 'dashboard/stores/deals';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Flag from 'dashboard/components-next/flag/Flag.vue';
import Policy from 'dashboard/components/policy.vue';
import CompaniesForm from 'dashboard/components-next/Companies/CompaniesForm/CompaniesForm.vue';
import ConfirmCompanyDeleteDialog from 'dashboard/components-next/Companies/CompanyDetail/ConfirmCompanyDeleteDialog.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import CompanyHistorySidebar from 'dashboard/components-next/Companies/CompanyDetail/CompanyHistorySidebar.vue';
import CompanyNotesSidebar from 'dashboard/components-next/Companies/CompanyDetail/CompanyNotesSidebar.vue';
import Timeline from 'dashboard/components-next/Timeline/Timeline.vue';
import CompanyContactsSidebar from 'dashboard/components-next/Companies/CompanyDetail/CompanyContactsSidebar.vue';
import DealInfo from 'dashboard/routes/dashboard/conversation/contact/DealInfo.vue';

const props = defineProps({
  id: { type: Number, required: true },
  name: { type: String, default: '' },
  domain: { type: String, default: '' },
  description: { type: String, default: '' },
  email: { type: String, default: '' },
  phoneNumber: { type: String, default: '' },
  city: { type: String, default: '' },
  countryCode: { type: String, default: '' },
  socialProfiles: { type: Object, default: () => ({}) },
  cnpj: { type: String, default: '' },
  customAttributes: { type: Object, default: () => ({}) },
  contactsCount: { type: Number, default: 0 },
  firstContacts: { type: Array, default: () => [] },
  avatarUrl: { type: String, default: '' },
  isExpanded: { type: Boolean, default: false },
  isUpdating: { type: Boolean, default: false },
  isDeleting: { type: Boolean, default: false },
  selectable: { type: Boolean, default: false },
  isSelected: { type: Boolean, default: false },
  createdAt: { type: [String, Number], default: null },
  lastActivityAt: { type: [String, Number], default: null },
  disableExpand: { type: Boolean, default: false },
});

const emit = defineEmits([
  'toggle',
  'updateCompany',
  'deleteCompany',
  'select',
  'nameClick',
]);

const { t } = useI18n();
const { relativeTime } = useLocalizedTime();
const companiesStore = useCompaniesStore();
const dealsStore = useDealsStore();
const companyDealsCount = ref(0);

// --- Company labels (read-only preview for the collapsed card header) ---
const companyLabelsStore = useCompanyLabelsStore();
const companyLabels = ref([]);

const labelColorMap = computed(() => companyLabelsStore.getLabelColorMap);

const fetchCompanyLabels = async () => {
  try {
    companyLabels.value = await companyLabelsStore.getCompanyLabels(props.id);
  } catch {
    companyLabels.value = [];
  }
};

const fetchCompanyDealsCount = async () => {
  try {
    const deals = await dealsStore.getByCompany(props.id);
    companyDealsCount.value = deals.length;
  } catch {
    companyDealsCount.value = 0;
  }
};

onMounted(async () => {
  await companyLabelsStore.fetchAll();
  await fetchCompanyLabels();
  fetchCompanyDealsCount();
});

const companiesFormRef = ref(null);
const confirmDeleteDialogRef = ref(null);
const [showDeleteSection, toggleDeleteSection] = useToggle();

const companyData = computed(() => ({
  id: props.id,
  name: props.name,
  domain: props.domain,
  description: props.description,
  email: props.email,
  phoneNumber: props.phoneNumber,
  city: props.city,
  countryCode: props.countryCode,
  socialProfiles: props.socialProfiles,
  cnpj: props.cnpj,
  customAttributes: props.customAttributes,
}));

const formData = ref({ ...companyData.value });

const isFormInvalid = computed(() => companiesFormRef.value?.isFormInvalid);

const countriesMap = computed(() =>
  countries.reduce((acc, c) => {
    acc[c.id] = c;
    return acc;
  }, {})
);

const countryDetails = computed(() => {
  if (!props.countryCode) return null;
  const c = countriesMap.value[props.countryCode];
  if (!c) return null;
  return { countryCode: c.id, name: c.name };
});

const displayName = computed(() => props.name || t('COMPANIES.UNNAMED'));
const avatarPreviewUrl = ref('');
const isUploadingAvatar = ref(false);
const avatarSource = computed(
  () => avatarPreviewUrl.value || props.avatarUrl || null
);

const createdAtFormatted = computed(() =>
  props.createdAt ? relativeTime(props.createdAt) : ''
);
const lastActivityAtFormatted = computed(() =>
  props.lastActivityAt ? relativeTime(props.lastActivityAt) : ''
);
const summary = computed(() =>
  [
    createdAtFormatted.value &&
      t('COMPANIES.DETAIL.PROFILE.CREATED_AT', {
        date: createdAtFormatted.value,
      }),
    lastActivityAtFormatted.value &&
      t('COMPANIES.DETAIL.PROFILE.LAST_ACTIVE', {
        date: lastActivityAtFormatted.value,
      }),
  ]
    .filter(Boolean)
    .join(' • ')
);

const handleAvatarUpload = async ({ file, url }) => {
  avatarPreviewUrl.value = url;
  isUploadingAvatar.value = true;
  try {
    await companiesStore.update({ id: props.id, avatar: file });
    useAlert(t('COMPANIES.DETAIL.AVATAR.UPLOAD_SUCCESS'));
  } catch {
    avatarPreviewUrl.value = '';
    useAlert(t('COMPANIES.DETAIL.AVATAR.UPLOAD_ERROR'));
  } finally {
    isUploadingAvatar.value = false;
  }
};

const handleAvatarDelete = async () => {
  try {
    await companiesStore.deleteCompanyAvatar(props.id);
    avatarPreviewUrl.value = '';
    useAlert(t('COMPANIES.DETAIL.AVATAR.DELETE_SUCCESS'));
  } catch {
    useAlert(t('COMPANIES.DETAIL.AVATAR.DELETE_ERROR'));
  }
};

const totalContacts = computed(() => Number(props.contactsCount || 0));
const hasContacts = computed(() => totalContacts.value > 0);
const contactsLabel = computed(() => {
  const first = props.firstContacts || [];
  if (!first.length) return '';
  const names = first.map(c => c.name).join(', ');
  const extra = totalContacts.value - first.length;
  return extra > 0 ? `${names} +${extra}` : names;
});

// --- Tabs (Conversas / Contatos vinculados / Negociação / Notas) ---
const SIDEBAR_TABS_OPTIONS = [
  { key: 'HISTORY', value: 'history' },
  { key: 'CONTACTS', value: 'contacts' },
  { key: 'DEALS', value: 'deals' },
  { key: 'NOTES', value: 'notes' },
];

const activeSidebarTab = ref('history');
const isHistoryPanelOpen = ref(false);
const selectedContactCandidate = ref(null);

const companyContacts = computed(() => companiesStore.companyContacts);
const companyContactsMeta = computed(() => companiesStore.companyContactsMeta);
const companyConversations = computed(
  () => companiesStore.companyConversations || []
);
const companyNotes = computed(() => companiesStore.companyNotes || []);
const contactSearchResults = computed(
  () => companiesStore.contactSearchResults
);
const sidebarUiFlags = computed(() => companiesStore.getUIFlags);
const isFetchingContacts = computed(
  () => sidebarUiFlags.value.fetchingContacts
);
const isFetchingConversations = computed(
  () => sidebarUiFlags.value.fetchingConversations
);
const isFetchingNotes = computed(() => sidebarUiFlags.value.fetchingNotes);
const isSearchingContacts = computed(
  () => sidebarUiFlags.value.searchingContacts
);
const isManagingContacts = computed(
  () =>
    sidebarUiFlags.value.creatingContact || sidebarUiFlags.value.removingContact
);

const sidebarTabs = computed(() =>
  SIDEBAR_TABS_OPTIONS.map(tab => ({
    label: {
      notes: t('COMPANIES.DETAIL.SIDEBAR.TABS.NOTES'),
      history: t('COMPANIES.DETAIL.SIDEBAR.TABS.HISTORY'),
      contacts: `${t('COMPANIES.DETAIL.SIDEBAR.TABS.CONTACTS')} (${totalContacts.value})`,
      deals:
        companyDealsCount.value > 0
          ? `${t('COMPANIES.DETAIL.SIDEBAR.TABS.DEALS')} (${companyDealsCount.value})`
          : t('COMPANIES.DETAIL.SIDEBAR.TABS.DEALS'),
    }[tab.value],
    value: tab.value,
  }))
);

const activeSidebarTabIndex = computed(() =>
  SIDEBAR_TABS_OPTIONS.findIndex(tab => tab.value === activeSidebarTab.value)
);

const clearSelectedContactCandidate = () => {
  selectedContactCandidate.value = null;
};

const loadSidebarTab = tabValue => {
  if (tabValue === 'notes') companiesStore.getCompanyNotes(props.id);
  if (tabValue === 'history') companiesStore.getCompanyConversations(props.id);
  if (tabValue === 'contacts') {
    companiesStore.getCompanyContacts(props.id, 1);
    companiesStore.searchCompanyContactCandidates({
      companyId: props.id,
      search: '',
    });
  }
};

const handleSidebarTabChange = tab => {
  activeSidebarTab.value = tab.value;
  loadSidebarTab(tab.value);
};

const handleContactSearch = async query => {
  await companiesStore.searchCompanyContactCandidates({
    companyId: props.id,
    search: query,
  });
};

const handleConfirmContactSelection = async () => {
  const candidate = selectedContactCandidate.value;
  if (!candidate) return;

  const isReassigning =
    candidate.company?.id && candidate.company.id !== props.id;
  const message = isReassigning
    ? t('COMPANIES.DETAIL.CONTACTS.MESSAGES.REASSIGN_SUCCESS')
    : t('COMPANIES.DETAIL.CONTACTS.MESSAGES.ADD_SUCCESS');

  try {
    await companiesStore.attachContactToCompany(props.id, candidate.id);
    useAlert(message);
    clearSelectedContactCandidate();
    companiesStore.searchCompanyContactCandidates({
      companyId: props.id,
      search: '',
    });
  } catch {
    const errorMessage = isReassigning
      ? t('COMPANIES.DETAIL.CONTACTS.MESSAGES.REASSIGN_ERROR')
      : t('COMPANIES.DETAIL.CONTACTS.MESSAGES.ADD_ERROR');
    useAlert(errorMessage);
  }
};

const handleRemoveContact = async contactId => {
  const currentPage = Number(companyContactsMeta.value.page || 1);
  const nextPage =
    currentPage > 1 && companyContacts.value.length === 1
      ? currentPage - 1
      : currentPage;

  try {
    await companiesStore.removeContactFromCompany(
      props.id,
      contactId,
      nextPage
    );
    useAlert(t('COMPANIES.DETAIL.CONTACTS.MESSAGES.REMOVE_SUCCESS'));
    companiesStore.searchCompanyContactCandidates({
      companyId: props.id,
      search: '',
    });
  } catch {
    useAlert(t('COMPANIES.DETAIL.CONTACTS.MESSAGES.REMOVE_ERROR'));
  }
};

const loadCompanyContactsPage = async page => {
  await companiesStore.getCompanyContacts(props.id, page);
};

watch(
  () => props.isExpanded,
  expanded => {
    if (!expanded) {
      showDeleteSection.value = false;
      fetchCompanyLabels();
      return;
    }
    activeSidebarTab.value = 'history';
    clearSelectedContactCandidate();
    loadSidebarTab('history');
  }
);

const handleFormUpdate = updated => {
  formData.value = { ...updated };
};

const onClickExpand = () => emit('toggle');

const handleUpdateCompany = () => {
  const data = companiesFormRef.value?.state || formData.value;
  emit('updateCompany', {
    id: props.id,
    name: data.name?.trim(),
    domain: data.domain?.trim() || null,
    description: data.description?.trim() || null,
    email: data.email?.trim() || null,
    phoneNumber: data.phoneNumber?.trim() || null,
    city: data.city?.trim() || null,
    countryCode: data.countryCode || null,
    socialProfiles: data.socialProfiles || {},
    cnpj: data.cnpj?.trim() || null,
    customAttributes: data.customAttributes || {},
  });
};

const openConfirmDeleteDialog = () => {
  confirmDeleteDialogRef.value?.dialogRef.open();
};

const handleConfirmDelete = () => {
  emit('deleteCompany', props.id);
};
</script>

<template>
  <div class="relative">
    <div
      v-if="selectable"
      class="absolute -left-2 top-1/2 -translate-y-1/2 z-10 flex items-center"
    >
      <input
        type="checkbox"
        :checked="isSelected"
        class="h-4 w-4 rounded border-n-weak text-n-blue-9 focus:ring-n-blue-8 cursor-pointer"
        @change="e => emit('select', e.target.checked)"
        @click.stop
      />
    </div>
    <CardLayout
      :key="id"
      layout="row"
      :class="selectable ? 'ltr:ml-6 rtl:mr-6' : ''"
    >
      <div class="flex items-center justify-start flex-1 gap-4">
        <Avatar
          :name="displayName"
          :src="avatarSource"
          class="shrink-0"
          :size="48"
          hide-offline-status
          rounded-full
          :allow-upload="isExpanded && !isUploadingAvatar"
          @upload="handleAvatarUpload"
          @delete="handleAvatarDelete"
        />
        <div class="flex flex-col gap-0.5 flex-1 min-w-0">
          <div class="flex flex-wrap items-center gap-x-4 gap-y-1 min-w-0">
            <span
              class="text-base font-medium truncate text-n-slate-12"
              :class="{ 'cursor-pointer hover:underline': disableExpand }"
              @click="disableExpand && emit('nameClick')"
            >
              {{ displayName }}
            </span>
            <span
              v-if="hasContacts"
              class="inline-flex items-center gap-1.5 text-sm text-n-slate-11 truncate"
            >
              <Icon icon="i-lucide-contact" size="size-3.5 text-n-slate-11" />
              {{ contactsLabel }}
            </span>
          </div>
          <div
            class="flex flex-wrap items-center justify-start gap-x-3 gap-y-1"
          >
            <span
              v-if="email"
              class="text-sm truncate text-n-slate-11"
              :title="email"
            >
              {{ email }}
            </span>
            <div
              v-if="email && (phoneNumber || domain || countryDetails)"
              class="w-px h-3 bg-n-slate-6"
            />
            <span v-if="phoneNumber" class="text-sm truncate text-n-slate-11">
              {{ phoneNumber }}
            </span>
            <div
              v-if="phoneNumber && (domain || countryDetails)"
              class="w-px h-3 bg-n-slate-6"
            />
            <span
              v-if="domain"
              class="inline-flex items-center gap-1.5 text-sm text-n-slate-11 truncate"
            >
              <Icon icon="i-lucide-globe" size="size-3.5 text-n-slate-11" />
              <span class="truncate">{{ domain }}</span>
            </span>
            <div
              v-if="domain && countryDetails"
              class="w-px h-3 bg-n-slate-6"
            />
            <span
              v-if="countryDetails"
              class="inline-flex items-center gap-2 text-sm truncate text-n-slate-11"
            >
              <Flag :country="countryDetails.countryCode" class="size-3.5" />
              {{ countryDetails.name }}
            </span>
            <div
              v-if="countryDetails && companyLabels.length"
              class="w-px h-3 bg-n-slate-6"
            />
            <span
              v-for="label in companyLabels"
              :key="label"
              class="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium text-white"
              :style="{ backgroundColor: labelColorMap[label] || '#6B7280' }"
            >
              {{ label }}
            </span>
          </div>
        </div>
      </div>

      <div v-if="!disableExpand" class="flex flex-col items-center gap-1">
        <Button
          icon="i-lucide-chevron-down"
          variant="ghost"
          color="slate"
          size="xs"
          :class="{ 'rotate-180': isExpanded }"
          @click="onClickExpand"
        />
        <Button
          v-if="isExpanded"
          icon="i-lucide-history"
          variant="ghost"
          :color="isHistoryPanelOpen ? 'blue' : 'slate'"
          size="xs"
          @click="isHistoryPanelOpen = !isHistoryPanelOpen"
        />
      </div>

      <template #after>
        <div
          class="transition-all duration-500 ease-in-out grid overflow-hidden"
          :class="
            isExpanded
              ? 'grid-rows-[1fr] opacity-100'
              : 'grid-rows-[0fr] opacity-0'
          "
        >
          <div class="overflow-hidden">
            <div class="flex items-start gap-6 p-6 border-t border-n-strong">
              <div
                class="flex flex-col gap-6 min-w-0"
                :class="isHistoryPanelOpen ? 'w-1/2' : 'flex-1'"
              >
                <!-- Summary: created at / last activity -->
                <span
                  v-if="summary"
                  class="inline-flex items-center gap-1 text-sm text-n-slate-11"
                >
                  <span class="i-ph-activity text-n-slate-10 size-4" />
                  {{ summary }}
                </span>
                <CompaniesForm
                  ref="companiesFormRef"
                  :company-data="companyData"
                  @update="handleFormUpdate"
                />

                <div>
                  <Button
                    :label="t('COMPANIES.DETAIL.PROFILE.ACTIONS.SAVE')"
                    size="sm"
                    :is-loading="isUpdating"
                    :disabled="isUpdating || isFormInvalid"
                    @click="handleUpdateCompany"
                  />
                </div>

                <!-- Tabs: Histórico / Contatos vinculados / Negociação / Notas -->
                <div class="flex flex-col gap-4 border-t border-n-strong pt-6">
                  <TabBar
                    :tabs="sidebarTabs"
                    :initial-active-tab="activeSidebarTabIndex"
                    class="w-full [&>button]:w-full bg-n-alpha-black2"
                    @tab-changed="handleSidebarTabChange"
                  />
                  <CompanyHistorySidebar
                    v-if="activeSidebarTab === 'history'"
                    :conversations="companyConversations"
                    :is-loading="isFetchingConversations"
                  />
                  <CompanyContactsSidebar
                    v-if="activeSidebarTab === 'contacts'"
                    :company="{ id, name, avatarUrl }"
                    :contacts="companyContacts"
                    :meta="companyContactsMeta"
                    :is-loading="isFetchingContacts"
                    :is-busy="isManagingContacts"
                    :search-results="contactSearchResults"
                    :is-searching="isSearchingContacts"
                    :selected-contact="selectedContactCandidate"
                    @cancel-contact-selection="clearSelectedContactCandidate"
                    @confirm-contact-selection="handleConfirmContactSelection"
                    @search="handleContactSearch"
                    @select-contact="
                      contact => (selectedContactCandidate = contact)
                    "
                    @remove-contact="handleRemoveContact"
                    @update:current-page="loadCompanyContactsPage"
                  />
                  <DealInfo
                    v-if="activeSidebarTab === 'deals'"
                    :company-id="id"
                    @deals-changed="count => (companyDealsCount = count)"
                  />
                  <CompanyNotesSidebar
                    v-if="activeSidebarTab === 'notes'"
                    :notes="companyNotes"
                    :is-loading="isFetchingNotes"
                  />
                </div>
              </div>

              <div
                v-if="isHistoryPanelOpen"
                class="flex flex-col gap-3 w-1/2 min-w-0 border-l border-n-strong pl-6"
              >
                <span class="text-sm font-medium text-n-slate-12">
                  {{ t('TIMELINE.TAB_LABEL') }}
                </span>
                <Timeline subject-type="Company" :subject-id="id" />
              </div>
            </div>

            <Policy :permissions="['administrator']">
              <div
                class="flex flex-col items-start border-t border-n-strong px-6 py-5"
              >
                <Button
                  :label="t('COMPANIES.DETAIL.DELETE.BUTTON')"
                  sm
                  link
                  slate
                  class="hover:!no-underline text-n-slate-12"
                  icon="i-lucide-chevron-down"
                  trailing-icon
                  @click="toggleDeleteSection()"
                />
                <div
                  class="transition-all duration-300 ease-in-out grid w-full overflow-hidden"
                  :class="
                    showDeleteSection
                      ? 'grid-rows-[1fr] opacity-100 mt-2'
                      : 'grid-rows-[0fr] opacity-0 mt-0'
                  "
                >
                  <div class="overflow-hidden min-h-0">
                    <span
                      class="inline-flex text-n-slate-11 text-sm items-center gap-1"
                    >
                      {{ t('COMPANIES.DETAIL.DELETE.SECTION_DESCRIPTION') }}
                      <Button
                        :label="t('COMPANIES.DETAIL.DELETE.CONFIRM')"
                        sm
                        ruby
                        link
                        @click="openConfirmDeleteDialog"
                      />
                    </span>
                  </div>
                </div>
              </div>

              <ConfirmCompanyDeleteDialog
                ref="confirmDeleteDialogRef"
                :company="{ id, name }"
                :is-loading="isDeleting"
                @confirm="handleConfirmDelete"
              />
            </Policy>
          </div>
        </div>
      </template>
    </CardLayout>
  </div>
</template>
